import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Button, Typography, Alert, Box, Stack, Avatar, Card, CardContent } from '@mui/material';
import { Inventory2 as Inventory2Icon, AddCircle as AddCircleIcon } from '@mui/icons-material';
import CreateContractItemComponent from '../component/CreateContractItemComponent';
import ContractItemsList from '../lists/contractItemsList';
import ProcurementContainer from '../../procurement/containers/ProcurementContainer';
import { useGetProcurementContractByIdQuery } from '../../../redux/procurementContractsApi';
import { formatCurrency } from '../../../utils/currencyUtil';

const APP_GRADIENT = 'linear-gradient(135deg, #1976d2 30%, #F7C914 90%)';

const ContractItemsPage: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  const { data: contractDetails } = useGetProcurementContractByIdQuery(
    { id: contractId ?? '' },
    { skip: !contractId }
  );

  if (!contractId) {
    return (
      <Alert severity="error">
        Contract ID is required. Please navigate from the Contract List page.
      </Alert>
    );
  }

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setCreateDialogOpen(true);
  };

  const handleCreateClick = () => {
    setEditingItem(null);
    setCreateDialogOpen(true);
  };

  const handleSuccess = () => {
    setCreateDialogOpen(false);
    setEditingItem(null);
  };

  const handleClose = () => {
    setCreateDialogOpen(false);
    setEditingItem(null);
  };

  return (
    <ProcurementContainer>
      <Stack spacing={3}>
        <Card
          sx={{
            background: APP_GRADIENT,
            color: 'white',
            padding: 3,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            marginTop: 1,
            marginX: 2,
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 2 }}>
            <Stack spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <Inventory2Icon fontSize="large" />
              </Avatar>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1.5, color: 'white' }}>
                {contractDetails?.title || 'Contract Items'}
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                {`Supplier: ${contractDetails?.supplier?.name || '-'} | Total Value: ${formatCurrency(Number(contractDetails?.totalValue ?? 0), 'usd')}`}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={handleCreateClick}
            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#125ea3' } }}
          >
            Add Contract Item
          </Button>

          <CreateContractItemComponent
            contractId={contractId}
            editingItem={editingItem}
            open={createDialogOpen}
            onClose={handleClose}
            onSuccess={handleSuccess}
          />
        </Paper>

        <ContractItemsList contractId={contractId} onEdit={handleEdit} />
      </Stack>
    </ProcurementContainer>
  );
};

export default ContractItemsPage;
